import posthog from "posthog-js";
import ConsentUtils from "../user/components/consent/ConsentUtils";
import User from "../user/types/User";

const posthogKey = <string>import.meta.env.VITE_POSTHOG_KEY ?? "";
const posthogHost = <string>import.meta.env.VITE_POSTHOG_HOST || "https://eu.i.posthog.com";
const envName = import.meta.env.VITE_APP_ENV ?? "dev";

const posthogEnabled = posthogKey !== "";

/**
 * Starts PostHog (analytics, session replay and error tracking) if a project key is configured.
 *
 * Respects the cookie consent banner: until the user accepts, PostHog keeps its state in memory
 * (no cookies or local storage) and doesn't record sessions. If they decline, nothing is captured.
 */
export function installPostHog() {
    if (!posthogEnabled) return;

    const consent = ConsentUtils.getConsentPermission();
    const apiHost = new URL(<string>import.meta.env.VITE_BASE_API_URL, window.location.origin).hostname;

    posthog.init(posthogKey, {
        api_host: posthogHost,
        defaults: "2026-05-30",
        person_profiles: "identified_only",
        persistence: consent === "true" ? "localStorage+cookie" : "memory",
        disable_session_recording: consent !== "true",
        capture_exceptions: true,
        // Lets the API link its errors and logs to this browser session.
        tracing_headers: [apiHost],
        loaded: (ph) => {
            // All three WalkBuddy apps share one PostHog project, so tag which one this is.
            ph.register({ app: "spa", environment: envName });
            if (consent === "false") {
                ph.opt_out_capturing();
            }
        },
    });
}

/**
 * Applies the user's answer to the cookie consent banner.
 */
export function setPostHogConsent(granted: boolean) {
    if (!posthogEnabled) return;

    if (granted) {
        posthog.opt_in_capturing();
        posthog.set_config({ persistence: "localStorage+cookie" });
        posthog.startSessionRecording();
    } else {
        posthog.stopSessionRecording();
        posthog.opt_out_capturing();
    }
}

/**
 * Links this browser to the signed-in user, and groups their activity by business.
 */
export function identifyPostHogUser(user: User) {
    if (!posthogEnabled) return;

    posthog.identify(String(user.id), {
        email: user.email,
        name: user.name,
    });

    if (user.business) {
        posthog.group("business", String(user.business.id), {
            name: user.business.name,
        });
    }
}

export function resetPostHogUser() {
    if (!posthogEnabled) return;
    posthog.reset();
}

export function capturePostHogEvent(eventName: string, properties?: Record<string, unknown>) {
    if (!posthogEnabled) return;
    posthog.capture(eventName, properties);
}

export function capturePostHogException(error: unknown, properties?: Record<string, unknown>) {
    if (!posthogEnabled) return;
    posthog.captureException(error, properties);
}
